import { useMemo } from 'react';

import ReactECharts from 'echarts-for-react';

import { InfoPopover } from '@/components/common/InfoPopover';
import { SectionTitle } from '@/components/common/SectionTitle';
import type { InventoryCoveragePolicy } from '@/features/decisions/decisions.api.types';
import type { ForecastQuantiles } from '@/features/decisions/utils/inventoryCoverageChartOption';
import { stockoutRiskByWeek, targetRiskFor, TIER_COLOR } from '@/features/decisions/utils/stockoutRisk';
import { buildStockoutRiskChartOption } from '@/features/decisions/utils/stockoutRiskChartOption';

interface Props {
  policy: InventoryCoveragePolicy | undefined;
  policyLabel: string;
  decisionDate: string;
  quantiles: ForecastQuantiles | undefined;
  isLoading: boolean;
  error: Error | null;
  // Reorder what-if from the shared controls bar (null = recommended).
  overrideReorder: number | null;
}

const fmtPct = (p: number, dp = 0) => `${(p * 100).toFixed(dp)}%`;
const fmtRisk = (p: number) => `${(p * 100).toFixed(p < 0.1 ? 1 : 0)}%`;
const fmtUnits = (n: number) => (n < 1 ? '<1' : Math.round(n).toLocaleString());
const fmtDay = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });

// Weekly stockout risk for the previewed policy — the simplest form: one bar per
// protection-period week showing the chance of at least one stockout by that
// week (cycle service level), from the forecast quantiles. Driven by the reorder
// quantity in the controls bar above.
export function StockoutRiskChart({
  policy,
  policyLabel,
  decisionDate,
  quantiles,
  isLoading,
  error,
  overrideReorder,
}: Props) {
  const targetRisk = targetRiskFor(policy);
  const weeks = useMemo(
    () => stockoutRiskByWeek(policy, decisionDate, quantiles, overrideReorder),
    [policy, decisionDate, quantiles, overrideReorder],
  );
  const chartOption = useMemo(
    () => buildStockoutRiskChartOption(weeks, policy, decisionDate, targetRisk),
    [weeks, policy, decisionDate, targetRisk],
  );

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">Loading weekly risk…</div>
    );
  }
  if (error) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
        <span className="font-medium">Weekly risk unavailable:</span> {error.message}
      </div>
    );
  }
  // No quantiles (e.g. the Incumbent baseline isn't served them) → nothing to show.
  if (weeks.length === 0) return null;

  const estimated = weeks.some((w) => w.source === 'estimated');
  const peak = weeks.reduce((a, b) => (b.stockoutRisk > a.stockoutRisk ? b : a));
  const onTarget = peak.stockoutRisk <= targetRisk;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <SectionTitle>
            Weekly Stockout Risk <span className="font-normal text-gray-400">· Previewing {policyLabel}</span>
          </SectionTitle>
          <p className="mt-0.5 text-xs text-gray-500">
            Chance of <span className="font-medium">at least one stockout by each week</span>, from the forecast
            quantiles at the reorder quantity above.
          </p>
        </div>
        <InfoPopover>
          <p className="font-semibold text-gray-800">How this is calculated</p>
          <p className="mt-1.5">
            For each week we read the cumulative-demand distribution by interpolating the forecast quantile curve
            directly — the same empirical quantiles the optimizer uses to size the reorder.
          </p>
          <p className="mt-1.5">
            <span className="font-medium text-gray-800">Stockout risk</span> = chance demand exceeds the inventory
            available that week:
          </p>
          <p className="mt-1 rounded bg-gray-50 px-2 py-1 font-mono text-[11px] text-gray-700">risk = 1 − F(S)</p>
          <p className="mt-1.5">
            <span className="font-medium text-gray-800">F</span> = demand CDF from the quantile grid;{' '}
            <span className="font-medium text-gray-800">S</span> = inventory available that week (on-hand during lead
            time, then on-hand + in-flight + reorder once it lands).
          </p>
          <p className="mt-1.5">
            <span className="font-medium text-gray-800">Expected units short</span> = E[(demand − S)⁺] from the same
            curve.
          </p>
          <p className="mt-1.5 text-gray-400">
            Forecast-implied; risk floors at the top served quantile. Colors are keyed to the policy&apos;s target
            service level.
          </p>
        </InfoPopover>
      </div>

      {/* Single verdict: peak risk vs target + expected magnitude */}
      <div
        className={`mb-3 rounded-md border px-3 py-2 text-xs ${
          onTarget ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-800'
        }`}
      >
        <span className="font-semibold">{onTarget ? '✓ On target.' : '⚠ Above target.'}</span> Peak{' '}
        {fmtRisk(peak.stockoutRisk)} chance of any stockout (week of {fmtDay(peak.period)}) vs {fmtPct(targetRisk)}{' '}
        target · ~{fmtUnits(peak.expectedUnitsShort)} Sale UOM short.
      </div>

      <ReactECharts opts={{ renderer: 'svg' }} option={chartOption} style={{ height: 280 }} notMerge />

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500">
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: TIER_COLOR.ok }} />
          On target (≤ {fmtPct(targetRisk)} risk)
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: TIER_COLOR.watch }} />
          Watch
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: TIER_COLOR.risk }} />
          At risk
        </span>
        <span className="text-sm font-medium text-gray-500">① Decision · ② Available · ③ Next available</span>
        {estimated && <span className="ml-auto text-gray-400">Estimated from forecast quantiles</span>}
      </div>
    </div>
  );
}
